import { Component } from '@/types';

export interface TooltipBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ViewportBounds {
  width: number;
  height: number;
}

export interface TooltipPlacement {
  x: number;
  y: number;
  placement: 'top' | 'bottom' | 'left' | 'right';
  arrowPosition?: { x: number; y: number };
}

/**
 * Calculates optimal tooltip positioning to keep it within viewport bounds
 *
 * @param component - The component to position the tooltip for
 * @param tooltipSize - Estimated or actual tooltip dimensions
 * @param offset - Offset from the component edge (default: 12px)
 * @param scale - Canvas zoom/scale factor (default: 1)
 * @returns Optimal tooltip position and placement
 */
export const calculateTooltipPosition = (
  component: Component,
  tooltipSize: { width: number; height: number },
  offset: number = 12,
  scale: number = 1
): TooltipPlacement => {
  // Get component element for accurate screen position
  const componentElement = document.querySelector(
    `[data-component-id="${component.id}"]`
  );

  if (!componentElement) {
    // Fallback to component position if element not found
    return {
      x: component.position.x * scale,
      y: component.position.y * scale - tooltipSize.height - offset,
      placement: 'top',
    };
  }

  const componentRect = componentElement.getBoundingClientRect();
  const viewport: ViewportBounds = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  // Calculate available space in each direction
  const spaceAbove = componentRect.top;
  const spaceBelow = viewport.height - componentRect.bottom;
  const spaceLeft = componentRect.left;
  const spaceRight = viewport.width - componentRect.right;

  // Determine optimal placement based on available space
  let placement: 'top' | 'bottom' | 'left' | 'right' = 'top';
  let x = componentRect.left;
  let y = componentRect.top - tooltipSize.height - offset;

  // Vertical placement (top vs bottom)
  if (spaceAbove >= tooltipSize.height + offset) {
    // Enough space above - use top placement (preferred)
    placement = 'top';
    y = componentRect.top - tooltipSize.height - offset;
  } else if (spaceBelow >= tooltipSize.height + offset) {
    // Not enough space above, but space below - use bottom placement
    placement = 'bottom';
    y = componentRect.bottom + offset;
  } else if (spaceLeft >= tooltipSize.width + offset) {
    // Not enough space above or below - try left
    placement = 'left';
    x = componentRect.left - tooltipSize.width - offset;
    y = componentRect.top;
  } else if (spaceRight >= tooltipSize.width + offset) {
    // Not enough space on left - try right
    placement = 'right';
    x = componentRect.right + offset;
    y = componentRect.top;
  } else {
    // Not enough space anywhere - place below with scrolling
    placement = 'bottom';
    y = componentRect.bottom + offset;
  }

  // Horizontal alignment for top/bottom placements
  if (placement === 'top' || placement === 'bottom') {
    // Try to center tooltip over component
    const centerX = componentRect.left + (componentRect.width / 2) - (tooltipSize.width / 2);

    // Check if centered position would overflow viewport
    if (centerX < 8) {
      // Would overflow left edge - align to left with padding
      x = 8;
    } else if (centerX + tooltipSize.width > viewport.width - 8) {
      // Would overflow right edge - align to right with padding
      x = viewport.width - tooltipSize.width - 8;
    } else {
      // Centered position fits
      x = centerX;
    }
  }

  // Vertical alignment for left/right placements
  if (placement === 'left' || placement === 'right') {
    // Try to vertically center tooltip with component
    const centerY = componentRect.top + (componentRect.height / 2) - (tooltipSize.height / 2);

    // Check if centered position would overflow viewport
    if (centerY < 8) {
      // Would overflow top edge
      y = 8;
    } else if (centerY + tooltipSize.height > viewport.height - 8) {
      // Would overflow bottom edge
      y = viewport.height - tooltipSize.height - 8;
    } else {
      // Centered position fits
      y = centerY;
    }
  }

  // Final boundary checks to ensure tooltip stays within viewport
  x = Math.max(8, Math.min(x, viewport.width - tooltipSize.width - 8));
  y = Math.max(8, Math.min(y, viewport.height - tooltipSize.height - 8));

  // Calculate arrow position (points to component center)
  const arrowPosition = {
    x: componentRect.left + componentRect.width / 2,
    y: componentRect.top + componentRect.height / 2,
  };

  return {
    x,
    y,
    placement,
    arrowPosition,
  };
};

/**
 * Checks if a tooltip would overflow viewport bounds
 */
export const wouldOverflowViewport = (
  tooltipBounds: TooltipBounds,
  viewport: ViewportBounds,
  padding: number = 8
): { overflows: boolean; edges: string[] } => {
  const edges: string[] = [];
  let overflows = false;

  if (tooltipBounds.x < padding) {
    edges.push('left');
    overflows = true;
  }

  if (tooltipBounds.y < padding) {
    edges.push('top');
    overflows = true;
  }

  if (tooltipBounds.x + tooltipBounds.width > viewport.width - padding) {
    edges.push('right');
    overflows = true;
  }

  if (tooltipBounds.y + tooltipBounds.height > viewport.height - padding) {
    edges.push('bottom');
    overflows = true;
  }

  return { overflows, edges };
};

/**
 * Adjusts tooltip position to account for canvas zoom/scale
 */
export const adjustForScale = (
  position: { x: number; y: number },
  scale: number,
  canvasOffset: { x: number; y: number } = { x: 0, y: 0 }
): { x: number; y: number } => {
  return {
    x: position.x * scale + canvasOffset.x,
    y: position.y * scale + canvasOffset.y,
  };
};

/**
 * Gets scroll-adjusted position for tooltip
 */
export const getScrollAdjustedPosition = (
  position: { x: number; y: number }
): { x: number; y: number } => {
  return {
    x: position.x + window.scrollX,
    y: position.y + window.scrollY,
  };
};
